<script setup lang="ts">
import StatusChangeConfirmModal from "@/components/Common/StatusCangeConfirmModal.vue"
import ResizableList from '@/components/ResizableList.vue';
import subManagementOfficeType from "@/types/subManagementOffice"
import { formatDate } from "@/services/functions"
import { reactive, defineProps, defineEmits } from 'vue'
import i18n from '@/lang/i18n'
import EditLinkIcon from "@/components/Common/EditLinkIcon.vue"
import DisplayFullnameFromUpdatedBy from '@/components/Common/DisplayFullnameFromUpdatedBy.vue';

const props = defineProps<{
  subManagementOffices: subManagementOfficeType[]
  changeStatusAuthority: boolean
  isLoading: boolean
  updatingStatus: boolean
}>()

const emits = defineEmits(['changeSortOrder', 'changeStatus'])
const HEADER = ([
  i18n.global.t('pages.subManagementOffice.id'),
  i18n.global.t('pages.subManagementOffice.companyName'),
  i18n.global.t('pages.subManagementOffice.contactName'),
  i18n.global.t('pages.subManagementOffice.dksNoInitial'),
  i18n.global.t('pages.subManagementOffice.telephoneNumber'),
  i18n.global.t('pages.subManagementOffice.comment'),
  i18n.global.t('pages.subManagementOffice.status'),
  i18n.global.t('pages.subManagementOffice.updatedAt'),
  i18n.global.t('pages.subManagementOffice.updatedBy'),
  i18n.global.t('pages.subManagementOffice.list.edit')
])
const SORTABLE_ITEMS: any = ({
  [i18n.global.t('pages.subManagementOffice.id')]: 'id',
  [i18n.global.t('pages.subManagementOffice.companyName')]: 'company_name',
  [i18n.global.t('pages.subManagementOffice.contactName')]: 'contact_name',
  [i18n.global.t('pages.subManagementOffice.dksNoInitial')]: 'dks_no_initial',
  [i18n.global.t('pages.subManagementOffice.telephoneNumber')]: 'telephone_number',
  [i18n.global.t('pages.subManagementOffice.status')]: 'status',
  [i18n.global.t('pages.subManagementOffice.updatedAt')]: 'updated_at',
  [i18n.global.t('pages.subManagementOffice.updatedBy')]: 'updated_by'
})
const currentSort = reactive({
  item: 'id',
  type: i18n.global.t('pages.common.sortType.asc')
})
const sortTypes = reactive([
  i18n.global.t('pages.common.sortType.asc'),
  i18n.global.t('pages.common.sortType.desc')
])
const itemName = i18n.global.t('pages.subManagementOffice.title.title')


function datetimeFormat (datetime: string) {
  const d = new Date(datetime)
  return formatDate(d)
}

function commnet (commnet: string | null) {
  if (commnet) return i18n.global.t('pages.subManagementOffice.list.existComment')
  return i18n.global.t('pages.subManagementOffice.list.notExistComment')
}

function emitChangeSortOrder (sortItem: string, sortType: string | null = null) {
  if (!sortableItem(sortItem)) return
  const item = SORTABLE_ITEMS[sortItem]
  if (sortType) {
    currentSort.type = sortType
  } else {
    if (currentSort.item == item) {
      const type = sortTypes.find(type => type != currentSort.type)
      currentSort.type = type ?? ""
    } else {
      currentSort.type = sortTypes[0]
    }
  }
  currentSort.item = item
  emits('changeSortOrder', currentSort.item, currentSort.type)
}

function sortableItem (itemName: string) {
  return Object.keys(SORTABLE_ITEMS).includes(itemName)
}

function addClassIfSortable (itemName: string) {
  if (!sortableItem(itemName)) return
  return 'sort-icon pl-2 pt-1';
}

function highLightIfCurrentSort (item: string, type: string) {
  if (currentSort.item != SORTABLE_ITEMS[item] || currentSort.type != type) return
  return 'fw-bold'
}

function applyLeftClass (index: number) {
  if (index == 0) return 'start-0'
}

function emitChangeStatus (params: {id: number, status: number}, key: string) {
  emits('changeStatus', params, key)
}
</script>

<template>
  <div class="sub-management-office-list">
    <template v-if="props.isLoading">
      <div class="list-loading">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".list"
    >
      <div class="list list-scrollbar">
        <v-table>
          <thead>
            <tr class="table-header-row">
              <th v-for="(item, index) in HEADER" :key="index" class="table-header column-space" :class="applyLeftClass(index)">
                <div class="d-flex">
                  <span v-if="sortableItem(item)" class="d-inline-block">
                    <div class="nc-icon nc-stre-up sort-icon d-block" @click="emitChangeSortOrder(item, sortTypes[0])" :class="highLightIfCurrentSort(item, sortTypes[0])" />
                    <div class="nc-icon nc-stre-down sort-icon d-block" @click="emitChangeSortOrder(item, sortTypes[1])" :class="highLightIfCurrentSort(item, sortTypes[1])" />
                  </span>
                  <span :class="addClassIfSortable(item)" @click="emitChangeSortOrder(item)">{{item}}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="subManagementOffices?.length != 0">
              <tr v-for="subManagementOffice in subManagementOffices" :key="subManagementOffice.id">
                <td class="column-space fix-td">{{subManagementOffice.id}}</td>
                <td class="column-space">{{subManagementOffice.company_name}}</td>
                <td class="column-space">{{subManagementOffice.contact_name ? subManagementOffice.contact_name : ''}}</td>
                <td class="column-space">{{subManagementOffice.dks_no_initial}}</td>
                <td class="column-space">{{subManagementOffice.telephone_number}}</td>
                <td class="column-space">{{commnet(subManagementOffice.comment)}}</td>
                <td class="column-space">
                  <StatusChangeConfirmModal
                    :item="subManagementOffice"
                    :itemName="itemName"
                    :updatableStatusButton="changeStatusAuthority"
                    :updatingStatus="updatingStatus"
                    @submit="emitChangeStatus"
                  />
                </td>
                <td class="column-space">{{datetimeFormat(subManagementOffice.updated_at)}}</td>
                <td class="column-space">
                  <DisplayFullnameFromUpdatedBy
                    :updatedBy="subManagementOffice.updated_by"
                  />
                </td>
                <td class="column-space">
                  <EditLinkIcon :name="'subManagementOfficeForm'" :id="subManagementOffice.id" />
                </td>
              </tr>
            </template>
            <template v-else-if="subManagementOffices?.length == 0 && !props.isLoading">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>
</template>
<style>
.sub-management-office-list .list table {
  position: absolute;
}
.sub-management-office-list .list td {
  word-break: keep-all;
}
.sub-management-office-list .list th {
  min-width: 7em;
  position: -webkit-sticky;
  position: sticky;
  background-color: white;
  top: 0;
  z-index: 1;
}
</style>
<style scoped>
.sub-management-office-list {
  position: relative;
}
.sub-management-office-list .list {
  position: relative;
  min-width: 58vw;
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
.sub-management-office-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
}
.sub-management-office-list .list .id-column {
  min-width: 2em;
}
.sub-management-office-list .list .updated-at-column {
  min-width: 10em;
}
.checkbox-in-grid {
  padding: 1vm;
  height: inherit !important;
  display: inline-grid !important;
}
.list-loading {
  position: absolute;
  min-width: 58vw;
  height: 100%;
  overflow: auto;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.list-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.column-space {
  white-space: nowrap;
}
.sub-management-office-list .sort-icon:hover {
  cursor: pointer;
  opacity: 0.5;
}
</style>
